<template>
    <div v-if="variant === 'default'" class="StatsBlock flex w-full items-center justify-center gap-0 sm:gap-12">
        <StatsBlockItem
            v-for="(item, index) in items"
            :key="index"
            class="flex flex-col items-center"
            :links="item.links"
        >
            <template #heading>
                <span>{{ item.start }}</span>
            </template>
            <template #text>
                <span class="text-sm">{{ item.text }}</span>
            </template>
        </StatsBlockItem>
    </div>

    <div v-else-if="variant === 'B2B'" class="StatsBlock flex w-full items-center justify-center gap-0 sm:gap-12">
        <StatsBlockItem v-for="(item, index) in items" :key="index" :links="item.links" variant="B2B">
            <template #heading>
                <span>{{ item.start }}</span>
            </template>
            <template #text>
                <span v-html="item.text"></span>
            </template>
        </StatsBlockItem>
    </div>
</template>

<script setup lang="ts">
import { useSettings } from '@/composables/settings/useSettings'
import { ref, onMounted, computed } from 'vue'

interface StatsItem {
    start: number
    heading: number
    text: string
    links: Array<{
        text: string
        to?: string | { name: string }
        href?: string
    }>
}

const props = withDefaults(
    defineProps<{
        variant?: string
    }>(),
    {
        variant: 'default'
    }
)

const applicationStore = useApplicationStore()

const isLoggedIn = computed(() => {
    return Boolean(applicationStore.userProfile && applicationStore.userPrivateProfile)
})

const items = ref<StatsItem[]>([
    {
        start: 1000,
        heading: 7000,
        text: ' zaregistrovaných používateľov',
        links: isLoggedIn.value ? 
            [
                {
                    text: 'zobraziť',
                    href: 'https://growni.sk/zaregistrovani-pouzivatelia'
                }
            ] : [
                {
                    text: 'pridaj sa',
                    to: { name: 'auth-create-account' }
                },
                {
                    text: 'zobraziť',
                    href: 'https://growni.sk/zaregistrovani-pouzivatelia'
                }
            ]
    },
    {
        start: 100,
        heading: 500,
        text: 'zapojených organizácií',
        links: [
            {
                text: 'zobraziť',
                to: { name: 'organizacie' }
            },
            {
                text: 'pridaj organizáciu',
                to: { name: 'profile-orgs-create' }
            }
        ]
    },
    {
        start: 100,
        heading: 1000,
        text: 'príležitostí na rast a rozvoj',
        links: [
            {
                text: 'zobraziť',
                href: 'https://growni.sk/prilezitosti-na-rozvoj'
            },
            {
                text: 'pridaj príležitosť',
                to: { name: 'profile-welcome' }
            }
        ]
    }
])

const increment = (duration: number = 2000) => {
    items.value.forEach((item) => {
        const startValue = item.start
        const endValue = item.heading
        const startTime = Date.now()

        const animate = () => {
            const currentTime = Date.now()
            const elapsed = currentTime - startTime
            const progress = Math.min(elapsed / duration, 1)

            // Easing function for smooth animation
            const easeOutQuad = (t: number) => t * (2 - t)
            const easedProgress = easeOutQuad(progress)

            item.start = Math.round(startValue + (endValue - startValue) * easedProgress)

            if (progress < 1) {
                requestAnimationFrame(animate)
            } else {
                item.start = endValue
            }
        }

        requestAnimationFrame(animate)
    })
}

onMounted(async () => {
    const { fetchOpportunitiesCount } = useSettings()
    const fetchedData = await fetchOpportunitiesCount('opportunitiesCount')

    items.value[0].heading = fetchedData.users
    items.value[1].heading = fetchedData.orgs
    items.value[2].heading = fetchedData.opportunities
    increment(2000) // 2 second animation duration
})
</script>
